import { c as _c } from "react/compiler-runtime";
import type { Edge as XyEdge, EdgeProps as XyEdgeProps } from '@xyflow/react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { cx } from 'class-variance-authority';
import useEmployeeExperiments from '@/hooks/use-employee-experiments';
import useCollapsedNodes from '../hooks/use-collapsed-nodes';
import useEdgeContext from '../hooks/use-edge-context';
import useNodeContext from '../hooks/use-node-context';
import useSidebar from '../hooks/use-sidebar';
import { BRANCH_EDGE_LABEL_OFFSET, EDGE_LABEL_AND_NODE_Z_INDEX, EDGE_Z_INDEX, HIGHLIGHTED_EDGE_CLASS_NAME } from '../playbook-builder.constants';
import type { EdgeMetadata } from '../utils/parse-graph/parse-graph.types';
export type EdgeProps = XyEdgeProps<XyEdge<EdgeMetadata>>;
const Edge = t0 => {
  const $ = _c(39);
  const {
    id,
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    style,
    data
  } = t0;
  const {
    successors
  } = useNodeContext(source);
  const {
    node: targetNode
  } = useNodeContext(target);
  const stepPosition = successors.length > 1 ? 0 : 1;
  let t1;
  if ($[0] !== sourceX || $[1] !== sourceY || $[2] !== stepPosition || $[3] !== targetX || $[4] !== targetY) {
    t1 = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      borderRadius: 0,
      stepPosition
    });
    $[0] = sourceX;
    $[1] = sourceY;
    $[2] = stepPosition;
    $[3] = targetX;
    $[4] = targetY;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const [edgePath] = t1;
  const {
    edgeColorStroke
  } = useEdgeContext(data?.sourceId ?? source, data?.targetId ?? target);
  const stroke = edgeColorStroke ?? "var(--border-color-primary)";
  const className = edgeColorStroke ? HIGHLIGHTED_EDGE_CLASS_NAME : "";
  const showArrow = !data?.isTargetLabel && targetNode?.kind !== "join_point" && targetNode?.kind !== "placeholder";
  const {
    isEnabled
  } = useEmployeeExperiments();
  let t2;
  if ($[6] !== isEnabled) {
    t2 = isEnabled("playbook-builder-debug-mode");
    $[6] = isEnabled;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isDebugModeEnabled = t2;
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  const t3 = `arrow-${id}`;
  let t4;
  if ($[8] !== stroke) {
    t4 = <path d="M 0 0 L 10 5 L 0 10 z" fill={stroke} />;
    $[8] = stroke;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <defs><marker id={t3} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4.8" markerHeight="4.8" orient="auto">{t4}</marker></defs>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== data?.collapsed) {
    t6 = data?.collapsed ? {
      strokeDasharray: "3 2"
    } : {};
    $[13] = data?.collapsed;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== stroke || $[16] !== style || $[17] !== t6) {
    t7 = {
      ...style,
      stroke,
      strokeWidth: 1.5,
      zIndex: EDGE_Z_INDEX,
      ...t6
    };
    $[15] = stroke;
    $[16] = style;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = showArrow ? `url(#arrow-${id})` : undefined;
  let t9;
  if ($[19] !== className || $[20] !== edgePath || $[21] !== id || $[22] !== t7 || $[23] !== t8) {
    t9 = <BaseEdge id={id} path={edgePath} style={t7} markerEnd={t8} className={className} />;
    $[19] = className;
    $[20] = edgePath;
    $[21] = id;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== data || $[26] !== midX || $[27] !== midY) {
    t10 = data?.collapsedLabel && data.collapsedSplitNodeId && <CollapsedEdgeLabel label={data.collapsedLabel} splitNodeId={data.collapsedSplitNodeId} posX={midX} posY={midY} />;
    $[25] = data;
    $[26] = midX;
    $[27] = midY;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== data || $[30] !== isDebugModeEnabled || $[31] !== sourceX || $[32] !== targetY) {
    t11 = isDebugModeEnabled && data?.minlen && <EdgeLabel label={data.minlen} posX={sourceX} posY={targetY} />;
    $[29] = data;
    $[30] = isDebugModeEnabled;
    $[31] = sourceX;
    $[32] = targetY;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t5 || $[37] !== t9) {
    t12 = <>{t5}{t9}{t10}{t11}</>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t5;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
const CollapsedEdgeLabel = t0 => {
  const $ = _c(13);
  const {
    label,
    splitNodeId,
    posX,
    posY
  } = t0;
  const {
    isAnyOpen
  } = useSidebar();
  const {
    toggleCollapsed
  } = useCollapsedNodes();
  let t1;
  if ($[0] !== splitNodeId || $[1] !== toggleCollapsed) {
    t1 = () => toggleCollapsed(splitNodeId);
    $[0] = splitNodeId;
    $[1] = toggleCollapsed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isAnyOpen) {
    t2 = cx(["inline-flex justify-center items-center gap-1.5 py-1 px-3", "border border-dashed border-tertiary rounded-full", "bg-primary text-caption-4 text-quaternary", "hover:border-accent hover:text-tertiary hover:bg-hover", "transition-colors cursor-pointer nodrag nopan text-nowrap", {
      "before:content-[\"\"] before:absolute before:inset-0 before:bg-secondary before:opacity-20 before:rounded-full": isAnyOpen
    }]);
    $[3] = isAnyOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `translate(-50%, -50%) translate(${posX}px,${posY}px)`;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      position: "absolute",
      transform: t3,
      pointerEvents: "all",
      zIndex: EDGE_LABEL_AND_NODE_Z_INDEX
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg width="8" height="8" viewBox="0 0 10 10" fill="currentColor"><path d="M2 3.5L5 7L8 3.5H2Z" /></svg>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label || $[9] !== t1 || $[10] !== t2 || $[11] !== t4) {
    t6 = <EdgeLabelRenderer><button type="button" onClick={t1} className={t2} style={t4}>{t5}{label}</button></EdgeLabelRenderer>;
    $[8] = label;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const EdgeLabel = t0 => {
  const $ = _c(8);
  const {
    label,
    posX,
    posY
  } = t0;
  const {
    isAnyOpen
  } = useSidebar();
  let t1;
  if ($[0] !== isAnyOpen) {
    t1 = cx(["inline-flex justify-center items-center gap-0.5 py-1 px-2", "border-[1.5px] border-solid border-primary rounded-full", "bg-primary text-caption-4 text-tertiary", "nodrag nopan", {
      "before:content-[\"\"] before:absolute before:inset-0 before:bg-secondary before:opacity-50 before:rounded-full": isAnyOpen
    }]);
    $[0] = isAnyOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `translate(-50%, -50%) translate(${posX}px,${posY - BRANCH_EDGE_LABEL_OFFSET}px)`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      position: "absolute",
      transform: t2,
      pointerEvents: "all",
      zIndex: EDGE_LABEL_AND_NODE_Z_INDEX
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label || $[5] !== t1 || $[6] !== t3) {
    t4 = <EdgeLabelRenderer><div className={t1} style={t3}>{label}</div></EdgeLabelRenderer>;
    $[4] = label;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export default Edge;